/**
 * Factory editor for Decision-agent tools (read / compute / write).
 * Tools are what make the definition executable in chat (REQ-AIS-039).
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useLanguage } from '@/state/language/LanguageProvider';
import {
  createVersionTool,
  deleteVersionTool,
  listPlatformToolHandlers,
  listVersionTools,
  seedCoreDecisionTools,
  type PlatformToolHandler,
} from '@/services/aiStudioAgentService';
import { STUDIO_TOOL_KINDS } from '@/types/aiStudioAgent';
import type { AiStudioVersionTool, StudioToolKind } from '@/types/aiStudioAgent';
import { FIELD_CLASS, LABEL_CLASS } from './constants';
import { FieldLabel } from './FieldLabel';

interface AgentToolsEditorProps {
  versionId: string;
  disabled?: boolean;
}

interface ToolDraft {
  toolName: string;
  toolKind: StudioToolKind;
  handlerRef: string;
  description: string;
}

const defaultDraft = (): ToolDraft => ({
  toolName: '',
  toolKind: 'read',
  handlerRef: '',
  description: '',
});

export const AgentToolsEditor: React.FC<AgentToolsEditorProps> = ({
  versionId,
  disabled = false,
}) => {
  const { t } = useLanguage();
  const [tools, setTools] = useState<AiStudioVersionTool[]>([]);
  const [handlers, setHandlers] = useState<PlatformToolHandler[]>([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState<ToolDraft>(defaultDraft);
  const [saving, setSaving] = useState(false);
  const [errorKey, setErrorKey] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      setTools(await listVersionTools(versionId));
    } catch {
      setErrorKey('aiStudio.definition.toolsLoadFailed');
    } finally {
      setLoading(false);
    }
  }, [versionId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  useEffect(() => {
    let cancelled = false;
    void listPlatformToolHandlers()
      .then((rows) => {
        if (!cancelled) setHandlers(rows);
      })
      .catch(() => {
        /* handler catalog optional */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const applyHandler = (handlerRef: string) => {
    const match = handlers.find((h) => h.handlerRef === handlerRef);
    if (!match) {
      setDraft((d) => ({ ...d, handlerRef }));
      return;
    }
    setDraft({
      toolName: match.defaultToolName,
      toolKind: match.toolKind,
      handlerRef: match.handlerRef,
      description: match.description,
    });
  };

  const handleAdd = async () => {
    if (!draft.toolName.trim() || !draft.handlerRef.trim() || !draft.description.trim()) return;
    setSaving(true);
    setErrorKey(null);
    try {
      const created = await createVersionTool(versionId, {
        toolName: draft.toolName.trim(),
        toolKind: draft.toolKind,
        handlerRef: draft.handlerRef.trim(),
        description: draft.description.trim(),
        sortOrder: tools.length * 10,
      });
      setTools((current) => [...current, created]);
      setDraft(defaultDraft());
    } catch {
      setErrorKey('aiStudio.definition.toolsSaveFailed');
    } finally {
      setSaving(false);
    }
  };

  const handleSeedCore = async () => {
    setSaving(true);
    setErrorKey(null);
    try {
      const created = await seedCoreDecisionTools(versionId);
      if (created.length) {
        setTools((current) => [...current, ...created]);
      } else {
        await reload();
      }
    } catch {
      setErrorKey('aiStudio.definition.toolsSaveFailed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (toolId: string) => {
    setSaving(true);
    setErrorKey(null);
    try {
      await deleteVersionTool(versionId, toolId);
      setTools((current) => current.filter((tool) => tool.id !== toolId));
    } catch {
      setErrorKey('aiStudio.definition.toolsSaveFailed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="flex flex-col gap-3">
      <h3 className="text-base font-semibold text-[var(--color-surface-text)]">
        {t('aiStudio.definition.toolsTitle')}
      </h3>
      <p className="text-base text-[var(--color-surface-text-secondary)]">
        {t('aiStudio.definition.toolsHint')}
      </p>

      {!disabled && (
        <button
          type="button"
          className="w-fit rounded border border-[var(--color-border)] px-3 py-2 text-base text-[var(--color-surface-text)] hover:border-[var(--color-primary-bg)] disabled:opacity-50"
          disabled={saving}
          onClick={() => void handleSeedCore()}
        >
          {t('aiStudio.definition.addCoreTools')}
        </button>
      )}

      {errorKey && (
        <p role="alert" className="text-base text-[var(--color-error)]">
          {t(errorKey)}
        </p>
      )}

      {loading ? (
        <p className="text-base text-[var(--color-surface-text-secondary)]">
          {t('aiStudio.definition.loading')}
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {tools.map((tool) => (
            <li
              key={tool.id}
              className="flex flex-wrap items-start justify-between gap-2 rounded border border-[var(--color-border)] px-3 py-2"
            >
              <div className="min-w-0 flex-1">
                <p className="font-medium text-[var(--color-surface-text)]">
                  {tool.toolName}{' '}
                  <span className="text-[var(--color-surface-text-secondary)]">
                    ({tool.toolKind})
                  </span>
                </p>
                <p className="text-base text-[var(--color-surface-text-secondary)]">
                  {tool.handlerRef}
                </p>
                <p className="text-base text-[var(--color-surface-text)]">{tool.description}</p>
              </div>
              {!disabled && (
                <button
                  type="button"
                  className="text-base text-[var(--color-error)] hover:underline"
                  disabled={saving}
                  onClick={() => void handleDelete(tool.id)}
                >
                  {t('aiStudio.definition.remove')}
                </button>
              )}
            </li>
          ))}
          {tools.length === 0 && (
            <li className="text-base text-[var(--color-surface-text-secondary)]">
              {t('aiStudio.definition.toolsEmpty')}
            </li>
          )}
        </ul>
      )}

      {!disabled && (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4">
          <div>
            <FieldLabel className={LABEL_CLASS}>{t('aiStudio.definition.handlerCatalog')}</FieldLabel>
            <select
              className={FIELD_CLASS}
              value={draft.handlerRef}
              onChange={(e) => applyHandler(e.target.value)}
            >
              <option value="">{t('aiStudio.definition.pickHandler')}</option>
              {handlers.map((h) => (
                <option key={h.handlerRef} value={h.handlerRef}>
                  {h.handlerRef} ({h.category})
                </option>
              ))}
            </select>
          </div>
          <div>
            <FieldLabel className={LABEL_CLASS}>{t('aiStudio.definition.toolName')}</FieldLabel>
            <input
              className={FIELD_CLASS}
              value={draft.toolName}
              onChange={(e) => setDraft((d) => ({ ...d, toolName: e.target.value }))}
            />
          </div>
          <div>
            <FieldLabel className={LABEL_CLASS}>{t('aiStudio.definition.toolKind')}</FieldLabel>
            <select
              className={FIELD_CLASS}
              value={draft.toolKind}
              onChange={(e) =>
                setDraft((d) => ({ ...d, toolKind: e.target.value as StudioToolKind }))
              }
            >
              {STUDIO_TOOL_KINDS.map((kind) => (
                <option key={kind} value={kind}>
                  {kind}
                </option>
              ))}
            </select>
          </div>
          <div>
            <FieldLabel className={LABEL_CLASS}>{t('aiStudio.definition.handlerRef')}</FieldLabel>
            <input
              className={FIELD_CLASS}
              value={draft.handlerRef}
              placeholder="run.get_instance_setup"
              onChange={(e) => setDraft((d) => ({ ...d, handlerRef: e.target.value }))}
            />
          </div>
          <div className="md:col-span-2 lg:col-span-4">
            <FieldLabel className={LABEL_CLASS}>{t('aiStudio.definition.toolDescription')}</FieldLabel>
            <input
              className={FIELD_CLASS}
              value={draft.description}
              onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
            />
          </div>
          <div>
            <button
              type="button"
              className="rounded bg-[var(--color-primary-bg)] px-3 py-2 text-base text-[var(--color-primary-text)] disabled:opacity-50"
              disabled={saving}
              onClick={() => void handleAdd()}
            >
              {t('aiStudio.definition.addTool')}
            </button>
          </div>
        </div>
      )}
    </section>
  );
};
